/**
 * UI-Bausteine. Bitte für alle Seiten verwenden, damit die App einheitlich aussieht.
 * Styles: src/styles.css
 */
import {
  createContext, forwardRef, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, Inbox, X, XCircle } from 'lucide-react';
import { centsToInput, fmtEur, parseEur } from '../lib/format';
import { ApiError } from '../lib/api';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'danger' | 'danger-solid' | 'success' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  block?: boolean;
  icon?: ReactNode;
  loading?: boolean;
  iconOnly?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'default', size = 'md', block, icon, loading, iconOnly, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'btn',
        variant === 'primary' && 'primary',
        variant === 'danger' && 'danger',
        variant === 'danger-solid' && 'danger solid',
        variant === 'success' && 'success',
        variant === 'ghost' && 'ghost',
        size !== 'md' && size,
        block && 'block',
        iconOnly && 'icon',
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : icon}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Karten & Seitenkopf
// ---------------------------------------------------------------------------
export function Card({ title, actions, children, footer, className, bodyClassName, noPadding }: {
  title?: ReactNode; actions?: ReactNode; children?: ReactNode; footer?: ReactNode; className?: string; bodyClassName?: string; noPadding?: boolean;
}) {
  return (
    <section className={cx('card', className)}>
      {(title || actions) && (
        <div className="card-header">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      <div className={cx(!noPadding && 'card-body', bodyClassName)}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Banner({ tone = 'info', children, action }: { tone?: 'info' | 'warn' | 'crit' | 'good'; children: ReactNode; action?: ReactNode }) {
  const Icon = tone === 'good' ? CheckCircle2 : tone === 'info' ? Info : tone === 'crit' ? XCircle : AlertTriangle;
  return (
    <div className={cx('banner', tone)} role={tone === 'crit' ? 'alert' : 'status'}>
      <Icon size={18} style={{ flex: 'none' }} />
      <div className="grow">{children}</div>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kennzahl-Kachel
// ---------------------------------------------------------------------------
export function Kpi({ label, value, sub, delta, deltaLabel }: {
  label: ReactNode; value: ReactNode; sub?: ReactNode;
  /** Relative Veränderung (0.12 = +12 %). Wird als farbiges Etikett gezeigt. */
  delta?: number | null; deltaLabel?: string;
}) {
  let deltaEl: ReactNode = null;
  if (delta != null && Number.isFinite(delta)) {
    const dir = Math.abs(delta) < 0.005 ? 'flat' : delta > 0 ? 'up' : 'down';
    const txt = `${delta > 0 ? '+' : ''}${(delta * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`;
    deltaEl = <span className={cx('delta', dir)} title={deltaLabel}>{dir === 'up' ? '▲' : dir === 'down' ? '▼' : '■'} {txt}</span>;
  }
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {(sub || deltaEl) && <div className="sub row" style={{ gap: 6 }}>{deltaEl}{sub && <span>{sub}</span>}{deltaLabel && deltaEl && <span>{deltaLabel}</span>}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badges, Leerzustand, Laden
// ---------------------------------------------------------------------------
export function Badge({ tone, children, title }: { tone?: 'good' | 'warn' | 'crit' | 'info'; children: ReactNode; title?: string }) {
  return <span className={cx('badge', tone)} title={title}>{children}</span>;
}

export function Empty({ icon, children, action }: { icon?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon ?? <Inbox size={32} />}
      <div>{children}</div>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

export function Spinner({ size = 22 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} role="status" aria-label="Lädt" />;
}

export function LoadingPage() {
  return <div className="loading-page"><Spinner size={32} /></div>;
}

/** Fehleranzeige für Query-Fehler. */
export function ErrorBox({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Unbekannter Fehler';
  return <Banner tone="crit" action={retry && <Button size="sm" onClick={retry}>Erneut versuchen</Button>}>{msg}</Banner>;
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: Array<{ value: T; label: ReactNode; hidden?: boolean }> }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.value} role="tab" aria-selected={t.value === value} className={cx(t.value === value && 'active')} onClick={() => onChange(t.value)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode }> }) {
  return (
    <div className="segmented" role="radiogroup">
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={cx(o.value === value && 'active')} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formularfelder
// ---------------------------------------------------------------------------
export function Field({ label, hint, error, children, className, htmlFor }: { label?: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; className?: string; htmlFor?: string }) {
  return (
    <div className={cx('field', className)}>
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <div className="error">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { size_?: 'lg' }>(function Input({ className, size_, ...rest }, ref) {
  return <input ref={ref} className={cx('input', size_ === 'lg' && 'lg', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return <select ref={ref} className={cx('select', className)} {...rest}>{children}</select>;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx('textarea', className)} {...rest} />;
});

export function Checkbox({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; disabled?: boolean }) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/**
 * Eingabefeld für Geldbeträge. Wert in Cent (number | null).
 * Akzeptiert "2,50", "2.50", "2". Formatiert beim Verlassen.
 */
export function MoneyInput({ value, onChange, placeholder = '0,00', autoFocus, id, size, disabled, allowNegative, onEnter, className, 'aria-label': ariaLabel, name }: {
  value: number | null; onChange: (cents: number | null) => void; placeholder?: string; autoFocus?: boolean; id?: string;
  size?: 'lg'; disabled?: boolean; allowNegative?: boolean; onEnter?: () => void; className?: string; 'aria-label'?: string; name?: string;
}) {
  const [text, setText] = useState(centsToInput(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(centsToInput(value));
  }, [value]);
  const invalid = text.trim() !== '' && (Number.isNaN(parseEur(text)) || (!allowNegative && parseEur(text) < 0));
  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        name={name}
        aria-label={ariaLabel}
        className={cx('input num', size === 'lg' && 'lg', className)}
        style={{ paddingRight: 28 }}
        inputMode="decimal"
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={invalid}
        onFocus={(e) => { focused.current = true; e.target.select(); }}
        onBlur={() => { focused.current = false; if (!invalid) setText(centsToInput(value)); }}
        onChange={(e) => {
          setText(e.target.value);
          const c = parseEur(e.target.value);
          onChange(e.target.value.trim() === '' || Number.isNaN(c) ? null : c);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) { e.preventDefault(); onEnter(); } }}
      />
      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }}>€</span>
    </div>
  );
}

export function Money({ cents, signed, className }: { cents: number | null | undefined; signed?: boolean; className?: string }) {
  const tone = signed && cents ? (cents > 0 ? 'good' : 'crit') : undefined;
  return <span className={cx('num', tone, className)}>{signed && cents && cents > 0 ? '+' : ''}{fmtEur(cents)}</span>;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------
export function Dialog({ open, onClose, title, children, footer, width = 'normal', dismissable = true, className }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode;
  width?: 'normal' | 'wide' | 'xwide'; dismissable?: boolean; className?: string;
}) {
  const titleId = useId();
  useEffect(() => {
    if (!open || !dismissable) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, dismissable]);
  if (!open) return null;
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={(e) => { if (dismissable && e.target === e.currentTarget) onClose(); }}>
      <div className={cx('dialog', width !== 'normal' && width, className)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="dialog-header">
          <h2 id={titleId}>{title}</h2>
          {dismissable && <Button variant="ghost" iconOnly size="sm" onClick={onClose} aria-label="Schließen"><X size={18} /></Button>}
        </div>
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Toasts & Bestätigungen (global über <UiProvider>)
// ---------------------------------------------------------------------------
type ToastKind = 'info' | 'success' | 'error';
interface ToastItem { id: number; kind: ToastKind; message: ReactNode }
interface ConfirmOpts {
  title: string; message?: ReactNode; confirmLabel?: string; danger?: boolean; cancelLabel?: string;
  /** Fokus auf „Abbrechen“ statt „OK“ (z. B. Altersprüfung: ein versehentliches Enter des Scanners bestätigt nichts) */
  focusCancel?: boolean;
}

interface UiApi {
  toast: (message: ReactNode, kind?: ToastKind) => void;
  /** Zeigt Fehler (ApiError oder Error) als roten Toast. */
  toastError: (e: unknown) => void;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
}

const UiCtx = createContext<UiApi | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmState, setConfirmState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const nextId = useRef(1);

  const toast = useCallback((message: ReactNode, kind: ToastKind = 'info') => {
    const id = nextId.current++;
    setToasts((t) => [...t.slice(-4), { id, kind, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6000 : 3500);
  }, []);
  const toastError = useCallback((e: unknown) => {
    toast(e instanceof ApiError || e instanceof Error ? e.message : 'Unbekannter Fehler', 'error');
  }, [toast]);
  const confirm = useCallback((opts: ConfirmOpts) => new Promise<boolean>((resolve) => setConfirmState({ ...opts, resolve })), []);

  const close = (v: boolean) => { confirmState?.resolve(v); setConfirmState(null); };

  return (
    <UiCtx.Provider value={{ toast, toastError, confirm }}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={cx('toast', t.kind === 'error' && 'error', t.kind === 'success' && 'success')}>
            {t.kind === 'success' ? <CheckCircle2 size={18} /> : t.kind === 'error' ? <XCircle size={18} /> : <Info size={18} />}
            <div>{t.message}</div>
          </div>
        ))}
      </div>
      <Dialog
        open={!!confirmState}
        onClose={() => close(false)}
        title={confirmState?.title ?? ''}
        footer={<>
          <Button autoFocus={!!confirmState?.focusCancel} onClick={() => close(false)}>{confirmState?.cancelLabel ?? 'Abbrechen'}</Button>
          <Button variant={confirmState?.danger ? 'danger-solid' : 'primary'} autoFocus={!confirmState?.focusCancel} onClick={() => close(true)}>{confirmState?.confirmLabel ?? 'OK'}</Button>
        </>}
      >
        {confirmState?.message ?? null}
      </Dialog>
    </UiCtx.Provider>
  );
}

export function useUi(): UiApi {
  const c = useContext(UiCtx);
  if (!c) throw new Error('useUi außerhalb von UiProvider');
  return c;
}
